<template>
  <v-card width="428">
    <v-card-title class="primary--text pa-4">
      Link property
    </v-card-title>
    <v-autocomplete
      :loading="isLoading"
      :search-input.sync="search"
      v-model="objectId"
      label="Object"
      clearable
      hide-selected
      placeholder="Type for search object"
      :items="objectsComputed"
      item-value="id"
      item-text="name"
      class="px-4"
    ></v-autocomplete>
    <v-select
      :menu-props="{ bottom: true, offsetY: true }"
      v-model="propertyForLinking"
      :loading="isLoadingProperty"
      :disabled="!properties.length && !isLoadingProperty"
      label="Property"
      clearable
      :items="properties"
      item-value="id"
      item-text="property"
      class="px-4"
    ></v-select>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn text @click="close">Cancel</v-btn>
      <v-btn
        text
        color="primary"
        @click="saveHandler"
      >
        save
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {
  defineComponent,
  onMounted,
  ref,
  watch,
  computed
} from '@vue/composition-api';
import { useMutation, useLazyQuery, useResult } from '@vue/apollo-composable';
import { useDebounceFn } from '@vueuse/core';
import gqlObjects from '@/api/objects/schemas.graphql';
import objectSchema from '@/api/objects/item.graphql';

export default defineComponent({
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    entity: {
      type: Object,
      required: true
    },
    filterBy: {
      type: Array,
      default: () => ['device', 'application', 'dataset', 'media']
    }
  },
  setup(props, { emit, root }) {
    const objectId = ref('unknown');
    const search = ref('');
    const propertyForLinking = ref(null);

    const loadDevice = useLazyQuery(
      objectSchema.load,
      {
        id: ''
      },
      {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only'
      }
    );

    const loadPropertyById = useLazyQuery(
      objectSchema.getObjectProperty,
      {
        id: ''
      },
      {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only'
      }
    );

    loadPropertyById.onResult(({ data }) => {
      objectId.value = data.objectProperty.object.id;
      propertyForLinking.value = props.entity.linkedPropertyId;
    });

    const properties = useResult(loadDevice.result, [], data => {
      return data.object.objectProperties.map(item => ({
        id: item.id,
        property: humanReadableValue(item, data.object.schema)
      }));
    });

    const loadDevices = useLazyQuery(gqlObjects.loadRaw, {
      filter: {
        name: {
          includes: ''
        },
        schemaType: {
          in: props.filterBy
        }
      }
    });

    const { mutate: updateObjectProperty } = useMutation(
      objectSchema.updateObjectProperty
    );

    watch(
      () => search.value,
      useDebounceFn(value => {
        if (!value) {
          loadDevices.variables.value.filter.name.includes = '';
        } else {
          loadDevices.variables.value.filter.name.includes = value;
        }
      }, 350)
    );

    watch(
      () => objectId.value,
      () => {
        if (objectId.value && objectId.value !== 'unknown') {
          loadDevice.variables.value.id = objectId.value;
          loadDevice.load();
        } else {
          loadDevice.result.value = null;
        }
      }
    );

    const objects = useResult(loadDevices.result, [], data => {
      return [...data.objects.map(item => item)];
    });

    const objectsComputed = computed(() => {
      if (loadPropertyById.result.value) {
        return [
          {
            name: loadPropertyById.result?.value.objectProperty.object.name,
            id: loadPropertyById.result?.value.objectProperty.object.id
          },
          ...objects.value
        ];
      } else {
        return [
          ...objects.value
        ];
      }
    });

    const humanReadableValue = (propertyItem, schemaProperties) => {
      let schema = schemaProperties;

      if (!schema) {
        return false;
      } else {
        let schemaOfProperty = schema.schemaProperties.find(
          item => item.property === propertyItem.property
        );

        if (schemaOfProperty?.description) {
          return schemaOfProperty.description;
        } else {
          return `${propertyItem.property} (No description provided)`;
        }
      }
    };

    const close = () => {
      emit('close');
    };

    const saveHandler = () => {
      if (objectId.value === null) {
        updateObjectProperty({
          input: {
            id: props.entity.id,
            patch: {
              linkedPropertyId: null
            }
          }
        }).then(() => {
          close();
        });
      } else {
        updateObjectProperty({
          input: {
            id: props.entity.id,
            patch: {
              linkedPropertyId: propertyForLinking.value
            }
          }
        }).then(() => {
          close();
        });
      }
    };

    onMounted(() => {
      loadDevices.load();

      if (props.entity.linkedPropertyId) {
        loadPropertyById.variables.value.id = props.entity.linkedPropertyId;
        loadPropertyById.load();
      }
    });

    return {
      saveHandler,
      close,
      objectId,
      isLoading: loadDevices.loading,
      isLoadingProperty: loadDevice.loading,
      objectsComputed,
      search,
      propertyForLinking,
      loadPropertyById,
      properties
    };
  }
});
</script>
